import type { Money } from '@/shared/api/types';
import {
  formatCents,
  formatCivilDate,
  formatDateTime,
  formatMoney,
  formatRate,
  parseCivilDate,
} from '@/shared/lib/format';
import { nomeDoProvedor } from '@/shared/lib/nomeDoProvedor';

/**
 * Uma linha de historico. Serve a rota generica (`EntityHistoryEntry`) e as rotas
 * proprias que vieram antes dela, como o historico da ficha do tomador.
 */
export interface HistoryRow {
  id: string;
  action: string;
  actor_label: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  occurred_at: string;
  /** Campos mudados, quando o servidor ja os calculou. */
  changed?: string[] | undefined;
  ip?: string | undefined;
  user_agent?: string | undefined;
  origin_hidden?: boolean | undefined;
  request_id?: string | undefined;
}

function pareceDinheiro(valor: unknown): valor is Money {
  return (
    typeof valor === 'object' &&
    valor !== null &&
    typeof (valor as Money).amount === 'number' &&
    typeof (valor as Money).currency === 'string' &&
    typeof (valor as Money).exponent === 'number'
  );
}

const instanteIso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * Valor do antes e do depois em texto de leitura.
 *
 * A trilha grava o estado como o servidor o tem: centavos, pontos-base, datas ISO,
 * objetos de dinheiro. A conversao segue as mesmas regras do resto da tela, pelo
 * nome do campo (`*_cents`, `*_bps`) ou pela forma do valor, para que o historico
 * nao mostre 150000 onde o cadastro mostra R$ 1.500,00.
 */
export function valorDoHistorico(campo: string, valor: unknown): string {
  if (valor === null || valor === undefined || valor === '') return '-';
  if (campo === 'provider_key' && typeof valor === 'string')
    return nomeDoProvedor(valor);
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Nao';
  if (typeof valor === 'number') {
    if (campo.endsWith('_cents') && Number.isSafeInteger(valor))
      return formatCents(valor);
    if (campo.endsWith('_bps') && Number.isSafeInteger(valor)) return formatRate(valor);
    return String(valor);
  }
  if (typeof valor === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(valor) && parseCivilDate(valor)) {
      return formatCivilDate(valor);
    }
    if (instanteIso.test(valor) && !Number.isNaN(Date.parse(valor))) {
      return formatDateTime(valor);
    }
    return valor;
  }
  if (pareceDinheiro(valor)) return formatMoney(valor);
  if (Array.isArray(valor)) {
    return valor.length === 0
      ? '-'
      : valor.map((item) => valorDoHistorico(campo, item)).join(', ');
  }
  return JSON.stringify(valor);
}

/** Campos que mudaram: os do servidor, ou a comparacao do antes com o depois. */
export function camposMudados(linha: HistoryRow): string[] {
  if (linha.changed) return linha.changed;
  const campos = Array.from(
    new Set([...Object.keys(linha.before ?? {}), ...Object.keys(linha.after ?? {})]),
  );
  return campos
    .filter(
      (campo) =>
        JSON.stringify(linha.before?.[campo]) !== JSON.stringify(linha.after?.[campo]),
    )
    .sort();
}
